/**
 * Document type vocabularies — single source for every upload picker
 * =================================================================
 *
 * The delivery cabinet's categories used to live inline in
 * ``DocumentPanel.js``. They now live here because the deal / recap
 * upload surface (``DealDocumentsPanel``) shows the SAME list, and two
 * copies of a vocabulary is how the two screens drift apart.
 *
 * Every label here must also exist in the backend's type-code tables
 * (``backend/document_naming/services/module_config.py``) or the
 * composed file name silently falls back to ``DOC``. The deal module
 * resolves its labels against ``DEAL_DOCUMENT_TYPE_CODES`` first and
 * then ``DELIVERY_TYPE_CODES``, so adding a delivery type below needs
 * ONE backend edit, not two.
 */

// Delivery cabinet — mirrors DELIVERY_TYPE_CODES in module_config.py.
// A category with no ``commodityType`` key is universal (Solids +
// Fuels); ``commodityType: 'fuels'`` restricts it to Fuels.
export const DELIVERY_DOCUMENT_CATEGORIES = [
    {
        id: 'what',
        label: 'What',
        color: '#9C27B0',
        types: [
            'Certificate of Weight (COW)',
            'Certificate of Sampling and Analysis',
            'Draft Survey Report',
            'Cargo Manifest',
            'Stowage Plan',
            'Material Safety Data Sheet (MSDS)'
        ]
    },
    {
        id: 'who',
        label: 'Who',
        color: '#8BC34A',
        types: [
            'Purchase Contract',
            'Sales Contract',
            'Payment - Letter of Credit (LC)',
            'Commercial Invoice',
            'Non-performance letter',
            'Letter of Indemnity (LOI)'
        ]
    },
    {
        id: 'how',
        label: 'How',
        color: '#FF9800',
        types: [
            'Bill of Lading (BL)',
            'Non-Negotiable Bills of Lading',
            'Certificate of Origin (COO)',
            'Statement of Facts (SOF)',
            'Mates Receipts',
            'ETA Notices'
        ]
    },
    // --- Sprint 4A: Fuels-only Bunkers category ---------------------
    // Conditionally included by the commodity filters below. Solids
    // deliveries see exactly the 3 universal categories above; Fuels
    // deliveries see those plus Bunkers.
    {
        id: 'bunkers',
        label: 'Bunkers',
        color: '#E53935',
        commodityType: 'fuels',
        types: [
            'Bunker Delivery Note (BDN)',
            'Supplier Invoice (Bunkers)',
            'Sampling Certificate'
        ]
    }
];

// Deal / Offer / Recap-only documents. These have no delivery
// equivalent: they are the paperwork of the deal itself rather than of
// the cargo. ``Customer confirmation``, ``Supplier nomination`` and
// ``Freight recap`` are already written by the system when a dispatch
// or a freight recap is sent — listing them here means a manual
// re-upload of one carries the same label instead of "Other".
//
// ``Quality certificate`` from the pre-May-2026 list is deliberately
// NOT shown: the delivery categories already offer two labels that
// resolve to the same COA code, and a third synonym only makes the
// picker harder to answer. Its mapping stays in the backend table so
// documents filed under it before May still resolve.
export const DEAL_RECAP_CATEGORY = {
    id: 'deal',
    label: 'Deal & Recap',
    color: '#4a6b25',
    types: [
        'Recap email',
        'Counter offer',
        'Contract draft',
        'Customer confirmation',
        'Supplier nomination',
        'Freight recap',
        'Hamster source',
        'Other'
    ]
};

// The deal/recap picker is a SUPERSET of the delivery picker: a recap
// receives cargo paperwork (BDNs, bills of lading, invoices) as well as
// its own. Deal & Recap leads because it is the common case on these
// screens.
export const DEAL_DOCUMENT_CATEGORIES = [
    DEAL_RECAP_CATEGORY,
    ...DELIVERY_DOCUMENT_CATEGORIES
];

/**
 * Delivery rule (strict): a fuels-only category is shown ONLY when the
 * record is known to be fuels. An unknown commodity hides it.
 */
export const filterCategoriesForCommodity = (categories, commodityType) =>
    categories.filter(cat => !cat.commodityType || cat.commodityType === commodityType);

/**
 * Deal / recap rule (lenient): same as above, EXCEPT that an unknown
 * commodity shows everything. Freight recaps carry no commodity flag,
 * and a chartering desk still files bunker delivery notes — hiding the
 * category there would leave them no honest option.
 */
export const filterDealCategoriesForCommodity = (categories, commodityType) =>
    categories.filter(
        cat => !cat.commodityType || !commodityType || cat.commodityType === commodityType
    );
